/* eslint-disable max-classes-per-file */

import moment from 'moment';
import { action, computed, makeAutoObservable, observable } from 'mobx';
import {
    alias,
    getDefaultModelSchema,
    identifier,
    list,
    object,
    primitive,
    serializable,
    serialize,
    update,
} from 'serializr';
import capitalize from 'lodash/capitalize';

// eslint-disable-next-line import/no-cycle
import Court from './Court';

class Ratings {
    @serializable @observable ntrp!: number;
    @serializable @observable ello!: number;
    @serializable @observable other!: number;

    constructor() {
        makeAutoObservable(this);
    }

    @action
    clone() {
        return new (this.constructor as any)().fromJSON(this.toJSON());
    }

    @action
    fromJSON(json: any) {
        update<this>(this, json);
        return this;
    }

    @action
    toJSON() {
        return serialize<this>(this);
    }
}

class Calendar {
    @serializable @observable date!: string;
    @serializable(list(primitive())) @observable time!: string[];

    constructor() {
        makeAutoObservable(this);
    }

    @action
    clone() {
        return new (this.constructor as any)().fromJSON(this.toJSON());
    }

    @action
    fromJSON(json: any) {
        update<this>(this, json);
        return this;
    }

    @action
    toJSON() {
        return serialize<this>(this);
    }
}

class Preference {
    @serializable(list(primitive())) @observable court!: string[];
    @serializable(list(primitive())) @observable play!: string[];
    @serializable(list(primitive())) @observable training!: string[];

    constructor() {
        makeAutoObservable(this);
    }

    @action
    clone() {
        return new (this.constructor as any)().fromJSON(this.toJSON());
    }

    @action
    fromJSON(json: any) {
        update<this>(this, json);
        return this;
    }

    @action
    toJSON() {
        return serialize<this>(this);
    }
}

export default class User {
    @serializable(alias('_id', identifier())) @observable id!: string;

    @serializable @observable image!: string;
    @serializable @observable gender!: string;
    @serializable @observable firstName!: string;
    @serializable @observable lastName!: string;
    @serializable @observable email!: string;
    @serializable @observable password!: string;
    @serializable(list(primitive())) @observable languages!: string[];
    @serializable @observable location!: string;
    @serializable @observable address!: string;
    @serializable @observable phone!: string;

    @serializable @observable age!: number;
    @serializable @observable experience!: number;

    @serializable(object(Preference)) @observable preference!: Preference;

    @serializable(object(Ratings)) @observable ratings!: Ratings;

    @serializable(list(object(Calendar))) @observable calendar: Calendar[] = [];

    // TODO: @serializable(list(object(User))
    @observable friends: User[] = [];
    @serializable(list(object(Court))) @observable courts: Court[] = [];

    @serializable @observable totalGames!: string;
    @serializable @observable rating!: string;

    @serializable @observable available!: string;

    @serializable @observable status!: string;
    @serializable @observable role!: string;

    @serializable @observable verified!: boolean;

    @serializable @observable visitedAt!: string;

    @computed
    get name() {
        return `${this.firstName || ''} ${this.lastName || ''}`.trim() || 'Undefined';
    }

    @computed
    get homeCourt() {
        return this.courts[0];
    }

    @computed
    get lastSeen() {
        if (this.visitedAt) {
            const today = moment();
            const visitedAt = moment(this.visitedAt);
            const month = today.diff(visitedAt, 'days');
            if (month < 20) {
                return `<span>Last visit: <strong>${capitalize(visitedAt.fromNow())}</strong></span>`;
            }
        }

        return '<span>Last visit: <strong>Recently</strong></span>';
    }

    @computed
    get readyToPlay() {
        const currCalendar = this.calendar;
        const currCalendarTimeOrder: any[] = ['<12', '12-6', '>6'];
        const currCalendarTimeMap: any = {
            '<12': 'Morning',
            '12-6': 'Afternoon',
            '>6': 'Evening',
        };
        const currCalendarFirst = currCalendar.find((c: any) => moment(c.date).startOf('day').isSameOrAfter(moment().startOf('day')));
        const currCalendarFirstDate = currCalendarFirst?.date || '';
        const currCalendarFirstTime = (currCalendarFirst?.time || [])
            .slice()
            .sort((a: any, b: any) => {
                const ai = currCalendarTimeOrder.indexOf(a);
                const bi = currCalendarTimeOrder.indexOf(b);
                return ai - bi;
            });

        return currCalendarFirstDate && currCalendarFirstTime.length
            ? `<span>Ready to play: <strong>${currCalendarTimeMap[currCalendarFirstTime[0]]}, ${moment(currCalendarFirstDate).format('MMM DD')}</strong></span>`
            : 'Not ready to play now';
    }

    constructor() {
        const schema = getDefaultModelSchema<this>(User);
        if (schema) {
            schema.props.friends = list(object(User));
        }

        makeAutoObservable(this);
    }

    @action
    clone() {
        return new (this.constructor as any)().fromJSON(this.toJSON());
    }

    @action
    fromJSON(json: any) {
        update<this>(this, json);
        return this;
    }

    @action
    toJSON() {
        return serialize<this>(this);
    }
}
